import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Loading, EmptyState } from "@/components/shared";
import { fmtNum } from "@/lib/format";
import { toast } from "sonner";
import { Trash2, FileUp, CalendarClock, CheckCircle2, AlertTriangle, Clock, FileText } from "lucide-react";

const STATUS_META = {
  never: { color: "#EF4444", label: "Never uploaded", icon: AlertTriangle },
  overdue: { color: "#EF4444", label: "Overdue", icon: AlertTriangle },
  due_soon: { color: "#F59E0B", label: "Due soon", icon: Clock },
  upcoming: { color: "#10B981", label: "On track", icon: CheckCircle2 },
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

export default function UploadRegisterTab({ onUpload }) {
  const [loading, setLoading] = useState(true);
  const [reg, setReg] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/uploads/register").then((r) => { setReg(r.data); setLoading(false); }).catch(() => { toast.error("Could not load register."); setLoading(false); });
  }, []);
  useEffect(load, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this upload and all its imported data?")) return;
    try { await api.delete(`/uploads/${id}`); toast.success("Upload removed"); load(); } catch { toast.error("Could not delete."); }
  };

  if (loading) return <Loading />;
  if (!reg) return null;

  return (
    <div className="space-y-6" data-testid="upload-register-tab">
      {/* Upcoming schedule */}
      <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden" data-testid="upload-schedule">
        <div className="p-5 border-b border-[#1a1a1a] flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-[#007AFF]" /><h3 className="font-head font-bold text-white">Upload Schedule · next 30 days</h3></div>
          {onUpload && <button data-testid="register-upload-btn" onClick={onUpload} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#007AFF] hover:bg-[#005BB5] text-white text-xs font-semibold"><FileUp className="h-3.5 w-3.5" /> Upload now</button>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#141414]">
          {reg.schedule.map((s) => {
            const m = STATUS_META[s.status] || STATUS_META.upcoming;
            const Icon = m.icon;
            const when = s.status === "never" ? "No upload on record" : s.days_until < 0 ? `${Math.abs(s.days_until)} day(s) overdue` : s.days_until === 0 ? "Due today" : `Due in ${s.days_until} day(s)`;
            return (
              <div key={s.type} data-testid={`schedule-${s.type}`} className="bg-[#0f0f0f] p-4 flex items-center gap-3">
                <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${m.color}22` }}><Icon className="h-4.5 w-4.5" style={{ color: m.color }} /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold truncate">{s.label}</p>
                  <p className="text-xs text-[#666]">Every {s.cadence_days} days · last {fmtDate(s.last_upload)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-semibold" style={{ color: m.color }}>{m.label}</p>
                  <p className="text-[11px] text-[#A3A3A3]">{when}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* History register */}
      <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden" data-testid="upload-history">
        <div className="p-5 border-b border-[#1a1a1a] flex items-center gap-2"><FileText className="h-4 w-4 text-[#10B981]" /><h3 className="font-head font-bold text-white">Uploaded Files</h3><span className="text-xs text-[#666]">{fmtNum(reg.history.length)} total</span></div>
        {!reg.history.length ? (
          <EmptyState title="No files uploaded yet" message="Upload your first Amazon report to start the register." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#666] text-xs uppercase tracking-wider">
                <th className="px-5 py-3">File</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Uploaded</th><th className="px-5 py-3 text-right">Rows</th><th className="px-5 py-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {reg.history.map((u) => (
                  <tr key={u.upload_id} data-testid={`upload-row-${u.upload_id}`} className="border-t border-[#141414] hover:bg-white/5">
                    <td className="px-5 py-3 text-white max-w-[280px] truncate" title={u.filename}>{u.filename}</td>
                    <td className="px-5 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#007AFF]/15 text-[#4da3ff]">{u.report_type}</span></td>
                    <td className="px-5 py-3 text-[#A3A3A3] tnum text-xs">{fmtDate(u.uploaded_at)}</td>
                    <td className="px-5 py-3 text-right tnum text-[#A3A3A3]">{fmtNum(u.records_ingested || 0)}</td>
                    <td className="px-5 py-3 text-right">{u.report_type !== "sample" && <button data-testid={`upload-delete-${u.upload_id}`} onClick={() => remove(u.upload_id)} className="text-[#A3A3A3] hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
